import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useChat, CHAT_COMING_SOON } from '../../hooks/useChat';
import SafaqatLogo from '../brand/SafaqatLogo';
import BrandRibbon from '../brand/BrandRibbon';
import { PLATFORM_LICENCES } from '../../config/platformLicences';
import { qrCodeSvg } from '../../utils/qrCode';
import type { ReactNode } from 'react';
import falMark from '../../assets/licences/fal.webp';
import regaMark from '../../assets/licences/rega.webp';
import mocMark from '../../assets/licences/moc.webp';

/**
 * The site footer.
 *
 * Owner, 2026-10-07: "cleaned up, nice to look at and easy to read". So: white
 * column headings at a readable size (no gold capitals, no red bar under each),
 * links at body size in a light teal that reads 9:1 on #002A23, every link in
 * one place only (terms and privacy were listed twice), the contact details
 * under the logo instead of a fourth column, and nothing that does not work yet
 * (the social icons pointed at "#", the live chat said «قريباً»).
 *
 * THEME.md: #002A23 is the one large green surface; the four-colour ribbon sits
 * on its top edge at 3px; the tagline is the gold line under the lockup.
 *
 * The licences (owner, 2026-10-07, "an image instead of numbers, like wasalt"):
 * two white badges, each the QR of the issuer's own public record beside the
 * issuer's marks (FAL + REGA; the Ministry of Commerce), cut from the documents
 * the owner supplied (assets/licences/). A badge opens that record; the numbers
 * travel in its accessible name, and the certificates stay one link away.
 */

/** Add the real account URLs here; the row shows once one is set. */
const SOCIAL_LINKS: { icon: string; label: string; href: string }[] = [
  { icon: 'ri-twitter-x-line', label: 'X', href: '' },
  { icon: 'ri-snapchat-line', label: 'Snapchat', href: '' },
  { icon: 'ri-instagram-line', label: 'Instagram', href: '' },
].filter((social) => social.href);

const HEADING = 'text-[0.9375rem] font-semibold text-white';
const LINK =
  'rounded text-[0.9375rem] text-[#BFD6D0] underline-offset-4 transition-colors hover:text-white hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60';

/**
 * A licence badge: white, like the official FAL badge the owner pointed to on
 * wasalt.sa. The QR encodes the issuer's public record (our own configured
 * URL, so the inline SVG is safe); the whole badge links there too.
 */
function LicenceBadge({ href, label, children }: { href: string; label: string; children: ReactNode }) {
  const qrSvg = qrCodeSvg(href, { ecc: 'MEDIUM', border: 1 });
  return (
    <a
      href={href}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      title={label}
      className="flex max-w-full items-center gap-3 rounded-[14px] bg-white py-2.5 pe-4 ps-2.5 shadow-[0_10px_28px_-14px_rgba(0,0,0,0.6)] transition-transform duration-200 hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/70 focus-visible:ring-offset-2 focus-visible:ring-offset-[#002A23] motion-reduce:transition-none sm:gap-4 sm:py-3 sm:pe-5 sm:ps-3"
    >
      <span
        aria-hidden="true"
        className="block h-14 w-14 shrink-0 sm:h-[68px] sm:w-[68px] [&>svg]:h-full [&>svg]:w-full"
        dangerouslySetInnerHTML={{ __html: qrSvg }}
      />
      <span aria-hidden="true" className="h-12 w-px shrink-0 bg-[#E2E8E6] sm:h-14" />
      <span aria-hidden="true" className="flex min-w-0 items-center gap-3 sm:gap-4">
        {children}
      </span>
    </a>
  );
}

export default function Footer() {
  const { t, i18n } = useTranslation('common');
  const { openChat } = useChat();
  const isRTL = i18n.dir() === 'rtl';

  // Explore: the public discovery pages (the logo already links home).
  const exploreLinks = [
    { to: '/search', label: t('footer.quickLinks.search', { defaultValue: 'Search for property' }) },
    { to: '/brokers/map', label: t('footer.quickLinks.brokersMap', { defaultValue: 'Brokers Map' }) },
    { to: '/trusted-brokers', label: t('footer.quickLinks.trustedBrokers', { defaultValue: 'Trusted Brokers' }) },
  ];

  // Services: list, subscribe, request.
  const serviceLinks = [
    { to: '/register', label: t('footer.serviceLinks.listProperty', { defaultValue: 'List Your Property' }) },
    { to: '/membership', label: t('footer.serviceLinks.membership', { defaultValue: 'Membership Plans' }) },
    { to: '/client-requests/new', label: t('footer.companyLinks.requests', { defaultValue: 'Request a Property' }) },
  ];

  // Company: who we are and how to reach us. Terms and privacy live once, in
  // the bottom line.
  const companyLinks = [
    { to: '/about', label: t('footer.companyLinks.about', { defaultValue: 'About Us' }) },
    { to: '/contact', label: t('footer.companyLinks.contact', { defaultValue: 'Contact Us' }) },
    { to: '/faq', label: t('footer.companyLinks.faq', { defaultValue: isRTL ? 'الأسئلة الشائعة' : 'FAQ' }) },
    { to: '/complaints', label: t('footer.companyLinks.complaints', { defaultValue: 'Submit a Complaint' }) },
    // Credits for the openly licensed photographs (the About page's #photo-credits section).
    { to: '/about#photo-credits', label: t('nav:footer.photoCredits', { defaultValue: 'Photo credits' }) },
  ];

  // The platform's own registrations (config/platformLicences.ts). Nothing is
  // invented: a licence with no number is hidden in production.
  const {
    legalName,
    falLicenseNumber,
    falVerifyUrl,
    falCertificateUrl,
    crNumber,
    crVerifyUrl,
    crCertificateUrl,
  } = PLATFORM_LICENCES;
  // A badge needs both its number and the issuer's public record.
  const showFal = Boolean(falLicenseNumber && falVerifyUrl);
  const showCr = Boolean(crNumber && crVerifyUrl);

  const linkColumn = (id: string, title: string, links: { to: string; label: string }[]) => (
    <nav aria-labelledby={id}>
      <h2 id={id} className={HEADING}>
        {title}
      </h2>
      <ul className="mt-4 space-y-3">
        {links.map((link) => (
          <li key={link.to}>
            <Link to={link.to} className={LINK}>
              {link.label}
            </Link>
          </li>
        ))}
      </ul>
    </nav>
  );

  return (
    <footer className="bg-[#002A23] text-[#BFD6D0]" dir={i18n.dir()}>
      {/* THEME.md §2.4c G1: the brand ribbon, once per page, 3px, on the
          footer's top edge (owner's standard, 2026-09-17). */}
      <BrandRibbon height={3} />

      {/* Full width with the navigation bar's own gutters, so the logo sits on
          the same edge as the logo in the header above. */}
      <div className="w-full px-5 pb-10 pt-12 sm:px-8 lg:px-10">
        <div className="grid grid-cols-2 gap-x-8 gap-y-10 md:grid-cols-3 lg:grid-cols-[minmax(0,1.5fr)_repeat(3,minmax(0,1fr))]">
          {/* The brand, then how to reach us */}
          <div className="col-span-2 md:col-span-3 lg:col-span-1">
            <Link
              to="/"
              className="inline-block rounded focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
              aria-label={t('brand.name')}
            >
              <SafaqatLogo tone="onDark" className="h-16 w-auto" title={t('brand.name')} />
            </Link>
            {/* the board's tagline, set right under the lockup as it is there */}
            {/* theme-allow-gold-fg: on #002A23, 10.24:1 */}
            <p className="mt-4 text-[0.9375rem] font-semibold text-[#FFC107]">
              {isRTL ? 'عقارك .. فرصتك الأفضل' : 'Your property .. your best opportunity'}
            </p>
            <p className="mt-1 max-w-sm text-[0.9375rem] leading-relaxed">
              {isRTL
                ? 'صفقات 24 - منصتك الموثوقة لأفضل المساكن والإيجارات الفاخرة.'
                : 'Safaqat 24 - Your trusted platform for distinguished residences and premium rentals.'}
            </p>

            <ul className="mt-5 space-y-2.5 text-[0.9375rem]">
              <li>
                <a href="tel:+966505608271" className={`inline-flex items-center gap-2 ${LINK}`}>
                  <i className="ri-phone-line text-[1.0625rem] leading-none text-[#7FC4B4]" aria-hidden="true"></i>
                  <span dir="ltr">{t('footer.phone')}</span>
                </a>
              </li>
              <li>
                <a href={`mailto:${t('footer.email')}`} className={`inline-flex items-center gap-2 ${LINK}`}>
                  <i className="ri-mail-line text-[1.0625rem] leading-none text-[#7FC4B4]" aria-hidden="true"></i>
                  <span>{t('footer.email')}</span>
                </a>
              </li>
              <li className="inline-flex items-center gap-2">
                <i className="ri-map-pin-line text-[1.0625rem] leading-none text-[#7FC4B4]" aria-hidden="true"></i>
                <span>{t('footer.address')}</span>
              </li>
              {!CHAT_COMING_SOON && (
                <li>
                  <button type="button" onClick={openChat} className={`inline-flex items-center gap-2 ${LINK}`}>
                    <i className="ri-customer-service-2-line text-[1.0625rem] leading-none text-[#7FC4B4]" aria-hidden="true"></i>
                    <span>{t('footer.liveChat')}</span>
                  </button>
                </li>
              )}
            </ul>

            {SOCIAL_LINKS.length > 0 && (
              <div className="mt-5 flex items-center gap-3">
                {SOCIAL_LINKS.map((social) => (
                  <a
                    key={social.label}
                    href={social.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={social.label}
                    className="flex h-10 w-10 items-center justify-center rounded-full border border-white/20 text-[#BFD6D0] transition-colors hover:border-white/50 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-white/60"
                  >
                    <i className={`${social.icon} text-[1.125rem]`} aria-hidden="true"></i>
                  </a>
                ))}
              </div>
            )}
          </div>

          {linkColumn('footer-explore', t('footer.quickLinksTitle', { defaultValue: 'Quick Links' }), exploreLinks)}
          {linkColumn('footer-services', t('footer.servicesTitle', { defaultValue: 'Services' }), serviceLinks)}
          {linkColumn('footer-company', t('footer.companyTitle', { defaultValue: 'Company' }), companyLinks)}
        </div>

        {(showFal || showCr) && (
          <div className="mt-10 border-t border-white/10 pt-8">
            <p className="text-[0.875rem] text-[#BFD6D0]">
              {isRTL ? 'الشركة المرخّصة: ' : 'Licensed company: '}
              <span lang="ar" className="font-semibold text-white">{legalName}</span>
            </p>
            <div className="mt-4 flex flex-wrap gap-4">
              {showFal && (
                <LicenceBadge
                  href={falVerifyUrl}
                  label={
                    isRTL
                      ? `رخصة فال للوساطة والتسويق رقم ${falLicenseNumber}، تحقق منها لدى الهيئة العامة للعقار`
                      : `FAL brokerage and marketing licence no. ${falLicenseNumber}, verify it with the Real Estate General Authority`
                  }
                >
                  <img src={falMark} alt="" width={198} height={89} className="h-8 w-auto sm:h-10" loading="lazy" decoding="async" />
                  <img src={regaMark} alt="" width={312} height={96} className="h-8 w-auto sm:h-10" loading="lazy" decoding="async" />
                </LicenceBadge>
              )}
              {showCr && (
                <LicenceBadge
                  href={crVerifyUrl}
                  label={
                    isRTL
                      ? `السجل التجاري، الرقم الوطني الموحد ${crNumber}، تحقق منه لدى المركز السعودي للأعمال`
                      : `Commercial registration, unified national no. ${crNumber}, verify it with the Saudi Business Center`
                  }
                >
                  <img src={mocMark} alt="" width={248} height={98} className="h-9 w-auto sm:h-11" loading="lazy" decoding="async" />
                  <span className="border-s border-[#E2E8E6] ps-3 text-[0.8125rem] font-semibold leading-tight text-[#1D2B29] sm:ps-4 sm:text-[0.875rem]">
                    {isRTL ? 'السجل التجاري' : 'Commercial registration'}
                  </span>
                </LicenceBadge>
              )}
            </div>
            {/* The certificates themselves, for whoever wants the document. */}
            <p className="mt-4 flex flex-wrap items-center gap-x-5 gap-y-1 text-[0.875rem]">
              {showFal && (
                <a href={falCertificateUrl} target="_blank" rel="noopener" className={`inline-flex items-center gap-1.5 ${LINK} text-[0.875rem]`}>
                  <i className="ri-file-pdf-2-line" aria-hidden="true"></i>
                  {isRTL ? 'رخصة فال (PDF)' : 'FAL licence (PDF)'}
                </a>
              )}
              {showCr && (
                <a href={crCertificateUrl} target="_blank" rel="noopener" className={`inline-flex items-center gap-1.5 ${LINK} text-[0.875rem]`}>
                  <i className="ri-file-pdf-2-line" aria-hidden="true"></i>
                  {isRTL ? 'شهادة السجل التجاري (PDF)' : 'Commercial registration certificate (PDF)'}
                </a>
              )}
            </p>
          </div>
        )}
      </div>

      {/* The bottom line: copyright, then the two legal pages. Kept short and to
          the start side, clear of the floating help buttons at the end. */}
      <div className="border-t border-white/10">
        <div className="flex w-full flex-wrap items-center gap-x-6 gap-y-2 px-5 py-5 text-[0.875rem] text-[#9DBDB6] sm:px-8 lg:px-10">
          <p>
            &copy; {new Date().getFullYear()} {t('brand.name')}. {t('footer.copyright')}
          </p>
          <Link to="/terms" className={`${LINK} text-[0.875rem]`}>
            {t('footer.terms')}
          </Link>
          <Link to="/privacy" className={`${LINK} text-[0.875rem]`}>
            {t('footer.privacy')}
          </Link>
        </div>
      </div>
    </footer>
  );
}
